import { aiFailureMessage, askArgosAi, type AiResult } from "./aiClient";

export type ChatTurn = { role: "user" | "assistant"; text: string };

type OpenAskOptions = {
  appName: string;
  system: string;
  question: string;
  history?: ChatTurn[];
  fallback: (question: string, history: ChatTurn[]) => string;
};

const MAX_REPLY_CHARS = 4000;

/** Cuts at the last sentence or line end so a long reply never stops mid-word. */
export function trimReply(text: string, max = MAX_REPLY_CHARS): string {
  const clean = text.trim();
  if (clean.length <= max) return clean;
  const head = clean.slice(0, max);
  const cut = Math.max(head.lastIndexOf(". "), head.lastIndexOf(".\n"), head.lastIndexOf("\n"), head.lastIndexOf("! "), head.lastIndexOf("? "));
  return (cut > max * 0.5 ? head.slice(0, cut + 1) : head.replace(/\s+\S*$/, "") + "…").trim();
}

async function askChat(system: string, messages: Array<{ role: "user" | "assistant"; content: string }>): Promise<AiResult> {
  // Temperatura más baja para reducir alucinaciones en “asistente”.
  const result = await askArgosAi([{ role: "system", content: system }, ...messages], { timeoutMs: 30000, temperature: 0.4 });
  if (!result.ok) return result;
  const text = trimReply(result.text);
  return text.length > 1 ? { ok: true, text } : { ok: false, text: "", reason: "unavailable" };
}

/** Preguntas abiertas con memoria del chat; prioriza respuesta útil y concreta. */
export async function openAsk(options: OpenAskOptions): Promise<string> {
  const history = options.history || [];
  const question = options.question.trim();
  if (!question) return options.fallback(question, history);

  // El historial ya incluye la pregunta actual al final: no la dupliquemos.
  const past = history.length && history[history.length - 1].role === "user" && history[history.length - 1].text.trim() === question
    ? history.slice(0, -1)
    : history;
  const recent: Array<{ role: "user" | "assistant"; content: string }> = past
    .slice(-8)
    .filter((turn) => turn.text.trim())
    .map((turn) => ({
      role: turn.role === "user" ? "user" : "assistant",
      content: turn.text,
    }));
  recent.push({ role: "user", content: question });

  const system = [
    `Eres el asistente de ${options.appName} (Rycso). Responde SIEMPRE en español, útil y concreto.`,
    "Sé breve: responde en 2-5 frases o una lista corta (máximo unas 120 palabras) y termina siempre la respuesta.",
    "Si el usuario pide ideas (qué hacer, qué dibujar, qué leer, adónde ir, qué cocinar…), da 4–8 opciones concretas, no genéricas.",
    "Si es un seguimiento («y qué…», «cuál», «dame más»), usa el contexto del chat anterior y profundiza.",
    "Si pregunta por la app, explica el flujo real (no inventes pantallas, botones ni funciones).",
    "Nunca inventes hechos, números, estados ni acciones realizadas. Si faltan datos para responder con certeza, dilo y ofrece una alternativa (qué revisar / qué preguntar / opciones).",
    "Privacidad: no reveles datos de otros usuarios. Si el usuario pide datos personales o internos que no están disponibles en el chat, explica la limitación sin adivinar.",
    options.system,
  ].join(" ");

  const ai = await askChat(system, recent);
  if (ai.ok) return ai.text;
  // Error claro: si la IA está saturada no fingimos una respuesta; si no hay conexión damos la ayuda sin IA.
  if (ai.reason === "busy") return aiFailureMessage("busy");
  const local = options.fallback(question, history);
  return `${aiFailureMessage(ai.reason ?? "unavailable")}\n\nMientras tanto: ${local}`;
}
